import React, { useState } from 'react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Plus, Pencil, Trash2, Loader2, ImageIcon } from 'lucide-react'
import { useToast } from '@/hooks/use-toast'
import {
  ClinicProgram,
  DisplayMode,
  clinicProgramService,
} from '@/services/clinicProgramService'
import { ClinicProgramOption } from '@/services/clinicProgramOptionsService'
import ProgramOptionFormDialog from '@/components/dialog/ProgramOptionFormDialog'
import ClinicProgramFormDialog from '@/components/dialog/ClinicProgramFormDialog'
import ClinicProgramImageDialog from '@/components/dialog/ClinicProgramImageDialog'
import ConfirmDeleteDialog from '@/components/ui/confirm-delete-dialog'
import DisplayModeToggle from '@/components/clinic-programs/DisplayModeToggle'
import { useGuidedProgramOptions } from '@/hooks/useGuidedProgramOptions'

const formatCurrency = (cents: number) => `$${(cents / 100).toFixed(2)}`

const frequencyLabel: Record<string, string> = {
  weekly: 'Weekly subscription',
  monthly: 'Monthly subscription',
  quarterly: 'Quarterly subscription',
}

interface GuidedProgramCardProps {
  program: ClinicProgram
  index: number
  clinicId: string
  onSuccess: () => void
  clinicArchetype?: 'guided' | 'direct' | 'hybrid' | null
}

const GuidedProgramCard: React.FC<GuidedProgramCardProps> = ({
  program,
  clinicId,
  onSuccess,
  clinicArchetype,
}) => {
  const { toast } = useToast()
  const [formOpen, setFormOpen] = useState(false)
  const [editingOption, setEditingOption] = useState<ClinicProgramOption | null>(null)
  const [editProgramOpen, setEditProgramOpen] = useState(false)
  const [deleteProgramOpen, setDeleteProgramOpen] = useState(false)
  const [imageDialogOpen, setImageDialogOpen] = useState(false)

  const {
    options,
    loading,
    isDeleting,
    deleteTarget,
    setDeleteTarget,
    fetchOptions,
    confirmDelete,
  } = useGuidedProgramOptions(clinicId, program.id, true)

  const handleFormSuccess = () => {
    setFormOpen(false)
    setEditingOption(null)
    fetchOptions()
  }

  const handleDeleteProgram = async () => {
    try {
      await clinicProgramService.deleteProgram(clinicId, program.id)
      onSuccess()
    } catch {
      toast({ title: 'Error', description: 'Failed to remove program', variant: 'destructive' })
    }
  }

  const handleDisplayModeUpdate = async (mode: DisplayMode) => {
    try {
      await clinicProgramService.updateProgram(clinicId, program.id, { display_mode: mode })
      toast({
        title: 'Updated',
        description: `Program routing changed to ${mode === 'protocol_showcase' ? 'Nimbus-Routed' : 'In-house'}`,
        variant: 'success',
      })
      onSuccess()
    } catch {
      toast({ title: 'Error', description: 'Failed to change program routing', variant: 'destructive' })
    }
  }

  const protocolLabel = program.protocol_label || program.title

  return (
    <Card className='overflow-hidden border border-slate-200 shadow-sm hover:shadow-md transition-shadow'>
      {clinicArchetype === 'hybrid' && (
        <div className='flex items-center justify-end px-4 pt-3'>
          <DisplayModeToggle
            currentMode={program.display_mode ?? 'protocol_showcase'}
            programId={program.id}
            clinicId={clinicId}
            programTitle={program.title}
            onUpdate={handleDisplayModeUpdate}
          />
        </div>
      )}

      <CardHeader className='pb-3'>
        <div className='flex items-start justify-between'>
          <div>
            <CardTitle className='text-base font-semibold text-slate-900'>{program.title}</CardTitle>
            {program.subtitle && <p className='mt-0.5 text-sm text-slate-500'>{program.subtitle}</p>}
            <p className='mt-1 text-xs text-blue-600 font-medium'>Protocol: {protocolLabel}</p>
          </div>
          {program.image_url && (
            <img src={program.image_url} alt={program.title} className='h-10 w-10 rounded-lg object-cover' />
          )}
        </div>
      </CardHeader>

      <CardContent className='space-y-3'>
        <div className='flex flex-wrap items-center gap-2 pb-2 border-b border-slate-100'>
          <Button
            variant='outline'
            size='sm'
            onClick={() => setEditProgramOpen(true)}
            className='h-8 text-xs font-medium border-slate-200 text-slate-600 hover:bg-slate-50 hover:text-slate-700'
          >
            <Pencil className='h-3.5 w-3.5' />Edit
          </Button>
          <Button
            variant='outline'
            size='sm'
            onClick={() => setDeleteProgramOpen(true)}
            className='h-8 text-xs font-medium border-slate-200 text-red-600 hover:bg-red-50 hover:text-red-700'
          >
            <Trash2 className='h-3.5 w-3.5' />Remove
          </Button>
          <Button
            variant='outline'
            size='sm'
            onClick={() => setImageDialogOpen(true)}
            className='h-8 text-xs font-medium border-slate-200 text-slate-600 hover:bg-slate-50 hover:text-slate-700'
          >
            <ImageIcon className='h-3.5 w-3.5' />Image
          </Button>
        </div>

        {loading ? (
          <div className='flex items-center justify-center py-4'>
            <Loader2 className='h-5 w-5 animate-spin text-slate-400' />
          </div>
        ) : options.length === 0 ? (
          <div className='rounded-lg border border-dashed border-slate-300 bg-slate-50 p-4 text-center'>
            <p className='text-sm text-slate-500'>No program options configured</p>
            <p className='text-xs text-slate-400 mt-1'>Add options to set program pricing</p>
          </div>
        ) : (
          <div className='space-y-2'>
            {options.map(option => (
              <div key={option.id} className='rounded-lg border border-slate-200 bg-white p-3'>
                <div className='flex items-start justify-between'>
                  <div className='flex-1'>
                    <p className='text-sm font-medium text-slate-900'>{option.label}</p>
                    {option.description && (
                      <p className='mt-0.5 text-xs text-slate-500 line-clamp-1'>{option.description}</p>
                    )}
                    <div className='mt-2 flex flex-wrap gap-3 text-xs'>
                      <span className='text-slate-600'>
                        Total Fee: <span className='font-semibold'>{formatCurrency(option.program_price)}</span>
                      </span>
                      <span className='text-slate-600'>
                        Clinic Fee: <span className='font-semibold'>{formatCurrency(option.clinic_facilitation_fee)}</span>
                      </span>
                      {typeof option.platform_program_fee === 'number' && (
                        <span className='text-blue-600'>
                          Nimbus Retains: <span className='font-semibold'>{formatCurrency(option.platform_program_fee)}</span>
                        </span>
                      )}
                    </div>
                    <div className='mt-1.5'>
                      <Badge variant='outline' className='text-[10px]'>
                        {option.is_subscription
                          ? frequencyLabel[option.subscription_frequency || 'monthly']
                          : 'One-time'}
                      </Badge>
                    </div>
                  </div>
                  <div className='flex gap-1 ml-2'>
                    <Button
                      variant='ghost'
                      size='icon'
                      className='h-7 w-7'
                      onClick={() => {
                        setEditingOption(option)
                        setFormOpen(true)
                      }}
                    >
                      <Pencil className='h-3.5 w-3.5' />
                    </Button>
                    <Button
                      variant='ghost'
                      size='icon'
                      className='h-7 w-7 text-red-500 hover:text-red-700'
                      onClick={() => setDeleteTarget(option)}
                    >
                      <Trash2 className='h-3.5 w-3.5' />
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        <Button
          variant='outline'
          size='sm'
          className='w-full'
          onClick={() => {
            setEditingOption(null)
            setFormOpen(true)
          }}
        >
          <Plus className='mr-1.5 h-3.5 w-3.5' />Add Option
        </Button>

        <ProgramOptionFormDialog
          open={formOpen}
          onOpenChange={(open) => {
            setFormOpen(open)
            if (!open) setEditingOption(null)
          }}
          clinicId={clinicId}
          programId={program.id}
          option={editingOption}
          onSuccess={handleFormSuccess}
        />

        {deleteTarget && (
          <ConfirmDeleteDialog
            open={!!deleteTarget}
            onOpenChange={(open) => !open && setDeleteTarget(null)}
            onConfirm={confirmDelete}
            title='Remove Option'
            description={`Are you sure you want to remove "${deleteTarget.label}"? This action cannot be undone.`}
            itemName={deleteTarget.label}
            isLoading={isDeleting}
          />
        )}

        {editProgramOpen && (
          <ClinicProgramFormDialog
            open={editProgramOpen}
            onOpenChange={setEditProgramOpen}
            onSuccess={onSuccess}
            clinicId={clinicId}
            mode='edit'
            program={program}
          />
        )}
        {imageDialogOpen && (
          <ClinicProgramImageDialog
            open={imageDialogOpen}
            onOpenChange={setImageDialogOpen}
            onSuccess={onSuccess}
            program={program}
            clinicId={clinicId}
          />
        )}
        {deleteProgramOpen && (
          <ConfirmDeleteDialog
            open={deleteProgramOpen}
            onOpenChange={setDeleteProgramOpen}
            title='Remove Program'
            description={`Are you sure you want to remove "${program.title}"? This action cannot be undone.`}
            itemName={program.title}
            itemType='program'
            onConfirm={handleDeleteProgram}
          />
        )}
      </CardContent>
    </Card>
  )
}

export default GuidedProgramCard
